import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import EmptyState from '../EmptyState';
import useIsMobile from './useIsMobile';
import {
  ST_COLORS,
  STATUS_ORDER,
  fmt,
  stLabel,
  projLabel,
  sortProjects,
  sortUnits,
} from './constants';

// ── UnitsBrowser ──
// Two-level inventory view (like the broker portal):
//   • Landing: a card per project with a status rollup → click to drill in.
//   • Drill-down: that project's units as cards, with status filter pills + back.
//   • Search: typing flattens to matching units across all projects (grouped).
function UnitsBrowser({ units, brandProjects, dbProjects, onRegisterDeal, onLaunchDeal, onAddUnit, onEdit, onCreateProject, onRenameProject, onDeleteProject, onDeleteUnit, t, lang }) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [activeProject, setActiveProject] = useState(null);
  const [stFilter, setStFilter] = useState('');
  const [search, setSearch] = useState('');
  const q = search.trim().toLowerCase();

  const matchUnit = (u) =>
    (u.unit_code || '').toLowerCase().includes(q) ||
    (u.client_name || '').toLowerCase().includes(q) ||
    (u.client_phone || '').toLowerCase().includes(q) ||
    (u.neighborhood || '').toLowerCase().includes(q) ||
    projLabel(u.project, t).toLowerCase().includes(q);

  // Landing: one rollup per project (brand projects with 0 units included).
  const projectRollups = useMemo(() => {
    const m = new Map();
    units.forEach((u) => {
      if (!m.has(u.project)) m.set(u.project, { project: u.project, total: 0, byStatus: {} });
      const r = m.get(u.project);
      r.total += 1;
      r.byStatus[u.status] = (r.byStatus[u.status] || 0) + 1;
    });
    (brandProjects || []).forEach((p) => { if (!m.has(p)) m.set(p, { project: p, total: 0, byStatus: {} }); });
    (dbProjects || []).forEach((p) => { if (!m.has(p)) m.set(p, { project: p, total: 0, byStatus: {} }); });
    return sortProjects([...m.keys()]).map((p) => m.get(p));
  }, [units, brandProjects, dbProjects]);

  // Search results across all projects, grouped by project.
  const searchGroups = useMemo(() => {
    if (!q) return [];
    const list = sortUnits(units.filter(matchUnit));
    const m = new Map();
    list.forEach((u) => { if (!m.has(u.project)) m.set(u.project, []); m.get(u.project).push(u); });
    return sortProjects([...m.keys()]).map((p) => ({ project: p, units: m.get(p) }));
  }, [units, q, t]);

  // Drill-down: the active project's units, filtered by status pill.
  const drillUnits = useMemo(() => {
    if (!activeProject) return [];
    return sortUnits(units.filter((u) => u.project === activeProject && (!stFilter || u.status === stFilter)));
  }, [units, activeProject, stFilter]);

  const drillStatusCounts = useMemo(() => {
    const scope = activeProject ? units.filter((u) => u.project === activeProject) : [];
    const c = { '': scope.length };
    STATUS_ORDER.forEach((s) => { c[s] = scope.filter((u) => u.status === s).length; });
    return c;
  }, [units, activeProject]);

  const totalUnits = units.length;

  // Create / rename a project (name only). onCreateProject/onRenameProject return
  // { error } on failure; on success the parent reloads and the card appears.
  const [projModal, setProjModal] = useState(null);
  const submitProj = async () => {
    const name = (projModal.value || '').trim();
    if (!name) { setProjModal((m) => ({ ...m, error: lang === 'ar' ? 'الاسم مطلوب' : 'Name required' })); return; }
    setProjModal((m) => ({ ...m, busy: true, error: '' }));
    const res = projModal.mode === 'create' ? await onCreateProject(name) : await onRenameProject(projModal.orig, name);
    if (res && res.error) { setProjModal((m) => ({ ...m, busy: false, error: res.error })); return; }
    if (projModal.mode === 'rename' && activeProject === projModal.orig) setActiveProject(name);
    setProjModal(null);
  };

  return (
    <div>
      {/* ── Action row ── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        {onLaunchDeal && (
          <button onClick={onLaunchDeal} className="btn btn-primary" style={{ padding: isMobile ? '10px 14px' : '8px 14px', fontSize: '0.82rem', fontWeight: 800, flex: isMobile ? '1 1 100%' : '0 0 auto', whiteSpace: 'nowrap', order: isMobile ? -1 : 0 }}>
            {lang === 'ar' ? '+ تسجيل صفقة' : '+ Register Deal'}
          </button>
        )}
        {onAddUnit && (
          <button onClick={onAddUnit} className="btn btn-secondary" style={{ padding: isMobile ? '10px 14px' : '8px 14px', fontSize: '0.82rem', fontWeight: 700, flex: isMobile ? '1 1 100%' : '0 0 auto', whiteSpace: 'nowrap', order: isMobile ? -1 : 0 }}>
            {lang === 'ar' ? '+ إضافة وحدة' : '+ Add Unit'}
          </button>
        )}
        {onCreateProject && (
          <button onClick={() => setProjModal({ mode: 'create', value: '', error: '' })} className="btn btn-secondary" style={{ padding: isMobile ? '10px 14px' : '8px 14px', fontSize: '0.82rem', fontWeight: 700, flex: isMobile ? '1 1 100%' : '0 0 auto', whiteSpace: 'nowrap', order: isMobile ? -1 : 0 }}>
            {lang === 'ar' ? '+ مشروع' : '+ Project'}
          </button>
        )}
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={lang === 'ar' ? 'بحث: وحدة، عميل، مشروع، حي...' : 'Search: unit, client, project, area...'}
          style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: '0.82rem', background: 'var(--bg-card)', flex: isMobile ? '1 1 100%' : '1 1 auto', minWidth: 200, maxWidth: isMobile ? '100%' : 320 }}
        />
        <span style={{ fontSize: '0.74rem', color: 'var(--text-secondary)', marginInlineStart: isMobile ? 0 : 'auto', fontWeight: 700 }}>
          {totalUnits} {lang === 'ar' ? 'وحدة' : 'units'}
        </span>
      </div>

      {projectRollups.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 16px' }}>
          <EmptyState icon={"🏘"} title={lang === 'ar' ? 'لا توجد مشاريع بعد' : 'No projects yet'} subtitle={lang === 'ar' ? 'أنشئ مشروعًا، ثم أضف وحداته لاحقًا' : 'Create a project, then add its units later'} />
          <div style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
            {onCreateProject && (
              <button onClick={() => setProjModal({ mode: 'create', value: '', error: '' })} className="btn btn-primary" style={{ padding: '10px 18px', fontSize: '0.86rem', fontWeight: 800 }}>
                {lang === 'ar' ? '+ إنشاء مشروع' : '+ Create Project'}
              </button>
            )}
            {onAddUnit && (
              <button onClick={onAddUnit} className="btn btn-secondary" style={{ padding: '10px 18px', fontSize: '0.86rem', fontWeight: 800 }}>
                {lang === 'ar' ? '+ إضافة وحدة' : '+ Add Unit'}
              </button>
            )}
          </div>
        </div>
      ) : q ? (
        /* ── Search results (flattened, grouped by project) ── */
        searchGroups.length === 0 ? (
          <EmptyState icon={"🔍"} title={lang === 'ar' ? 'لا توجد وحدات مطابقة' : 'No matching units'} subtitle={lang === 'ar' ? 'جرّب كلمة بحث أخرى' : 'Try a different search'} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {searchGroups.map((g) => (
              <ProjectSection key={g.project} title={projLabel(g.project, t)} units={g.units} onRegisterDeal={onRegisterDeal} onEdit={onEdit} t={t} lang={lang} />
            ))}
          </div>
        )
      ) : activeProject ? (
        /* ── Drill-down: one project's units ── */
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <button
                onClick={() => { setActiveProject(null); setStFilter(''); }}
                className="btn btn-secondary"
                style={{ padding: '7px 14px', fontSize: '0.82rem', fontWeight: 700, whiteSpace: 'nowrap' }}
              >
                {lang === 'ar' ? '→ كل المشاريع' : '← All Projects'}
              </button>
              <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800 }}>{projLabel(activeProject, t)}</h3>
              {onRenameProject && (
                <button onClick={() => setProjModal({ mode: 'rename', orig: activeProject, value: projLabel(activeProject, t), error: '' })} className="btn btn-secondary" style={{ padding: '5px 10px', fontSize: '0.74rem', fontWeight: 700, whiteSpace: 'nowrap' }} title={lang === 'ar' ? 'إعادة تسمية المشروع' : 'Rename project'}>
                  ✏️ {lang === 'ar' ? 'تعديل الاسم' : 'Rename'}
                </button>
              )}
              {onDeleteProject && (
                <button onClick={async () => {
                  const cnt = units.filter((x) => x.project === activeProject).length;
                  const msg = lang === 'ar'
                    ? `حذف المشروع «${projLabel(activeProject, t)}» وكل وحداته (${cnt})؟ لا يمكن التراجع.`
                    : `Delete project "${projLabel(activeProject, t)}" and all its units (${cnt})? This cannot be undone.`;
                  if (!window.confirm(msg)) return;
                  const res = await onDeleteProject(activeProject);
                  if (res && !res.error) setActiveProject(null);
                }} className="btn btn-secondary" style={{ padding: '5px 10px', fontSize: '0.74rem', fontWeight: 700, whiteSpace: 'nowrap', color: 'var(--error)', borderColor: 'rgba(239,68,68,0.4)' }} title={lang === 'ar' ? 'حذف المشروع' : 'Delete project'}>
                  🗑 {lang === 'ar' ? 'حذف المشروع' : 'Delete project'}
                </button>
              )}
            </div>
            <button
              onClick={() => navigate('/leads?project=' + encodeURIComponent(activeProject))}
              className="btn btn-secondary"
              style={{ padding: '7px 12px', fontSize: '0.78rem', fontWeight: 700, whiteSpace: 'nowrap', borderColor: '#8b5cf6', color: '#8b5cf6', background: 'rgba(139, 92, 246, 0.06)' }}
              title={lang === 'ar' ? 'عرض العملاء المهتمين بهذا المشروع' : 'View leads for this project'}
            >
              {lang === 'ar' ? 'عرض العملاء →' : 'View Leads →'}
            </button>
          </div>

          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 14 }}>
            <StatusPill active={!stFilter} onClick={() => setStFilter('')} label={lang === 'ar' ? 'الكل' : 'All'} count={drillStatusCounts['']} color="var(--accent)" />
            {STATUS_ORDER.map((s) => (drillStatusCounts[s] > 0 ? (
              <StatusPill key={s} active={stFilter === s} onClick={() => setStFilter(stFilter === s ? '' : s)} label={stLabel(s, lang)} count={drillStatusCounts[s]} color={ST_COLORS[s]} />
            ) : null))}
          </div>

          {drillUnits.length === 0 ? (
            <EmptyState icon={"📦"} title={lang === 'ar' ? 'لا توجد وحدات بهذه الحالة' : 'No units in this status'} />
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 10 }}>
              {drillUnits.map((u) => (
                <UnitCard key={u.id} u={u} onRegisterDeal={onRegisterDeal} onEdit={onEdit} onDeleteUnit={onDeleteUnit} t={t} lang={lang} />
              ))}
            </div>
          )}
        </div>
      ) : (
        /* ── Landing: project cards with status rollups ── */
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
          {projectRollups.map((p) => (
            <ProjectCard key={p.project} p={p} onClick={() => { setActiveProject(p.project); setStFilter(''); }} t={t} lang={lang} />
          ))}
        </div>
      )}

      {projModal && (
        <div onClick={() => !projModal.busy && setProjModal(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 20, width: '100%', maxWidth: 380, border: '1px solid var(--border)' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: '1.05rem', fontWeight: 800 }}>
              {projModal.mode === 'create' ? (lang === 'ar' ? 'مشروع جديد' : 'New Project') : (lang === 'ar' ? 'إعادة تسمية المشروع' : 'Rename Project')}
            </h3>
            <input
              autoFocus
              value={projModal.value}
              onChange={(e) => setProjModal((m) => ({ ...m, value: e.target.value }))}
              onKeyDown={(e) => { if (e.key === 'Enter') submitProj(); }}
              placeholder={lang === 'ar' ? 'اسم المشروع' : 'Project name'}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: '0.9rem', background: 'var(--bg-secondary)', color: 'var(--text-primary)', boxSizing: 'border-box' }}
            />
            {projModal.error && <div style={{ color: '#ef4444', fontSize: '0.78rem', marginTop: 8 }}>{projModal.error}</div>}
            <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" disabled={projModal.busy} onClick={() => setProjModal(null)}>{lang === 'ar' ? 'إلغاء' : 'Cancel'}</button>
              <button className="btn btn-primary" disabled={projModal.busy} onClick={submitProj}>{projModal.busy ? '…' : (lang === 'ar' ? 'حفظ' : 'Save')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Project rollup card (landing). Shows total + per-status breakdown; click to drill.
function ProjectCard({ p, onClick, t, lang }) {
  const get = (s) => p.byStatus[s] || 0;
  const available = get('متاح') + get('بيع على الخارطة');
  const sold = get('مباع') + get('أفرغ');
  return (
    <button
      onClick={onClick}
      style={{ textAlign: lang === 'ar' ? 'right' : 'left', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: 14, cursor: 'pointer', transition: 'all 0.15s', display: 'flex', flexDirection: 'column', gap: 10 }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)' }}>{projLabel(p.project, t)}</div>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: 3 }}>{p.total} {lang === 'ar' ? 'وحدة' : 'units'}</div>
        </div>
        <span style={{ background: available > 0 ? '#22c55e' : '#94a3b8', color: '#fff', padding: '5px 12px', borderRadius: 12, fontSize: '0.8rem', fontWeight: 800, whiteSpace: 'nowrap' }}>
          {available} {lang === 'ar' ? 'متاح' : 'avail.'}
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
        <MiniStat label={stLabel('متاح', lang)} value={get('متاح')} color={ST_COLORS['متاح']} />
        <MiniStat label={stLabel('محجوز', lang)} value={get('محجوز')} color={ST_COLORS['محجوز']} />
        <MiniStat label={stLabel('مباع', lang)} value={sold} color={ST_COLORS['مباع']} />
        <MiniStat label={get('بيع على الخارطة') > 0 ? stLabel('بيع على الخارطة', lang) : '—'} value={get('بيع على الخارطة')} color={ST_COLORS['بيع على الخارطة']} />
      </div>
    </button>
  );
}

function MiniStat({ label, value, color }) {
  return (
    <div style={{ textAlign: 'center', padding: '7px 5px', background: color + '12', borderRadius: 6 }}>
      <div style={{ fontSize: '1.15rem', fontWeight: 800, color, lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: '0.66rem', color: 'var(--text-secondary)', fontWeight: 700, marginTop: 3 }}>{label}</div>
    </div>
  );
}

// A titled project section used in search results.
function ProjectSection({ title, units, onRegisterDeal, onEdit, t, lang }) {
  const avail = units.filter((u) => u.status === 'متاح').length;
  return (
    <section>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, marginBottom: 10, paddingBottom: 6, borderBottom: '2px solid var(--border)' }}>
        <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800 }}>{title}</h3>
        <span style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', fontWeight: 700, whiteSpace: 'nowrap' }}>
          {units.length} {lang === 'ar' ? 'وحدة' : 'units'}
          {avail > 0 && <span style={{ color: ST_COLORS['متاح'], marginInlineStart: 8 }}>· {avail} {lang === 'ar' ? 'متاح' : 'available'}</span>}
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 10 }}>
        {units.map((u) => (
          <UnitCard key={u.id} u={u} onRegisterDeal={onRegisterDeal} onEdit={onEdit} t={t} lang={lang} />
        ))}
      </div>
    </section>
  );
}

// One unit as a compact card. Status color shows as a side stripe + badge.
function UnitCard({ u, onRegisterDeal, onEdit, onDeleteUnit, t, lang }) {
  const color = ST_COLORS[u.status] || '#888';
  const canClose = u.status === 'متاح' || u.status === 'بيع على الخارطة';
  const hasDeal = u.status === 'محجوز' || u.status === 'مباع' || u.status === 'أفرغ';
  // Only available (no-deal) units can be deleted; a sold/reserved one would orphan a deal.
  const canDelete = onDeleteUnit && !hasDeal;
  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderInlineStart: `3px solid ${color}`, borderRadius: 10, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-primary)' }}>{u.unit_code}</span>
        <StatusBadge status={u.status} color={color} lang={lang} />
      </div>
      <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
        {u.unit_type || '—'}{u.neighborhood ? ' · ' + u.neighborhood : ''}
      </div>
      <div style={{ direction: 'ltr', textAlign: lang === 'ar' ? 'right' : 'left', fontWeight: 800, fontSize: '1rem', color: 'var(--accent)' }}>
        {fmt(u.current_price || u.base_price)} <span style={{ fontSize: '0.66rem', color: 'var(--text-secondary)', fontWeight: 600 }}>SAR</span>
      </div>

      {hasDeal && (u.client_name || Number(u.earnest_money) > 0 || u.closer) && (
        <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', display: 'flex', flexDirection: 'column', gap: 2, borderTop: '1px dashed var(--border)', paddingTop: 6 }}>
          {u.client_name && <span>{lang === 'ar' ? 'العميل' : 'Client'}: <b style={{ color: 'var(--text-primary)' }}>{u.client_name}</b></span>}
          {Number(u.earnest_money) > 0 && <span style={{ direction: 'ltr', textAlign: lang === 'ar' ? 'right' : 'left' }}>{lang === 'ar' ? 'العربون' : 'Earnest'}: {fmt(u.earnest_money)}</span>}
          {u.closer && <span>{lang === 'ar' ? 'المنفّذ' : 'Closer'}: <b style={{ color: 'var(--accent)' }}>{u.closer}</b></span>}
        </div>
      )}

      {((canClose && onRegisterDeal) || onEdit || canDelete) && (
        <div style={{ display: 'flex', gap: 6, marginTop: 'auto', paddingTop: 4 }}>
          {canClose && onRegisterDeal && (
            <button onClick={() => onRegisterDeal(u)} className="btn btn-primary btn-sm" style={{ flex: 1, padding: '8px 10px', fontSize: '0.8rem', fontWeight: 800, whiteSpace: 'nowrap' }}>
              {t('udRegisterDeal')}
            </button>
          )}
          {onEdit && (
            <button
              onClick={() => onEdit(u)}
              style={{ flex: 1, padding: '8px 10px', fontSize: '0.8rem', fontWeight: 700, background: hasDeal ? 'rgba(99,102,241,0.10)' : 'transparent', border: '1.5px solid ' + (hasDeal ? 'var(--accent)' : 'var(--border)'), color: hasDeal ? 'var(--accent)' : 'var(--text-primary)', borderRadius: 6, cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              {hasDeal ? (lang === 'ar' ? 'تحديث الصفقة' : 'Update Deal') : t('udEdit')}
            </button>
          )}
          {canDelete && (
            <button
              onClick={() => { if (window.confirm(lang === 'ar' ? `حذف الوحدة ${u.unit_code}؟` : `Delete unit ${u.unit_code}?`)) onDeleteUnit(u); }}
              title={lang === 'ar' ? 'حذف الوحدة' : 'Delete unit'}
              style={{ padding: '8px 10px', fontSize: '0.8rem', fontWeight: 700, background: 'transparent', border: '1.5px solid rgba(239,68,68,0.4)', color: 'var(--error)', borderRadius: 6, cursor: 'pointer' }}
            >
              🗑
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status, color, lang }) {
  return (
    <span style={{ padding: '3px 9px', borderRadius: 12, fontSize: '0.66rem', fontWeight: 700, background: color + '18', color, whiteSpace: 'nowrap' }}>
      {stLabel(status, lang)}
    </span>
  );
}

function StatusPill({ active, onClick, label, count, color }) {
  return (
    <button
      onClick={onClick}
      style={{ padding: '6px 11px', borderRadius: 14, border: active ? '1.5px solid ' + color : '1px solid var(--border)', background: active ? color : 'var(--bg-card)', color: active ? '#fff' : 'var(--text-secondary)', fontSize: '0.74rem', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5, transition: 'all 0.12s' }}
    >
      <span>{label}</span>
      <span style={{ background: active ? 'rgba(255,255,255,0.25)' : color + '18', color: active ? '#fff' : color, padding: '0 6px', borderRadius: 8, fontSize: '0.62rem', minWidth: 18, textAlign: 'center' }}>{count}</span>
    </button>
  );
}

export default UnitsBrowser;
